import { Observable, from, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as mammoth from 'mammoth';
import { PdfExtractorService } from '@services/file-translations/file-translation-validation/text-from-file-extraction/pdf-text-extraction.service'
import { EmlTextExtractorService } from './eml-text-extraction.service';
import {
  MsgTextExtractorService
} from "@services/file-translations/file-translation-validation/text-from-file-extraction/msg-text-extraction.service";
import {
  TextCleanUpService
} from "@services/file-translations/file-translation-validation/text-clean-up/text-clean-up.service";

const textCleanUpService = new TextCleanUpService();
const pdfExtractorService = new PdfExtractorService(textCleanUpService);
const emlExtractorService = new EmlTextExtractorService(textCleanUpService);
const msgExtractorService = new MsgTextExtractorService(textCleanUpService);

/**
 * Extracts text content from different file types (.msg, .eml, .pdf, .txt, .docx)
 * @param file The file to extract text from
 * @returns Observable that emits the extracted text or error
 */
export function extractTextFromFile(file: File): Observable<string> {
  if (!file) {
    return throwError(() => new Error('Invalid file provided'));
  }

  const fileExtension = getFileExtension(file.name);

  switch (fileExtension) {
    case 'txt':
      return extractFromTxt(file);
    case 'pdf':
      return extractFromPdf(file);
    case 'docx':
      return extractFromDocx(file);
    case 'eml':
      return extractFromEml(file);
    case 'msg':
      return extractFromMsg(file);
    default:
      return throwError(() => new Error(`Unsupported file type: ${fileExtension}`));
  }
}

function getFileExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
}

export function extractFromTxt(file: File): Observable<string> {
  return from(
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        if (e.target?.result) {
          const textContent = e.target.result as string;
          // Clean up the text content
          const cleanedText = textCleanUpService.createSample(textContent);
          resolve(cleanedText);
        } else {
          reject(new Error('Failed to read text content'));
        }
      };

      reader.onerror = () => reject(new Error('Error reading text file'));
      reader.readAsText(file);
    })
  );
}

export function extractFromPdf(file: File): Observable<string> {
  return from(pdfExtractorService.extractTextFromPDF(file))
    .pipe(
        catchError(error => throwError(() => new Error(`PDF extraction failed: ${error.message || 'Unknown error'}`)))
    );
}

export function extractFromDocx(file: File): Observable<string> {
  return from(
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (e) => {
        if (!e.target?.result) {
          reject(new Error('Failed to read file content'));
          return;
        }

        try {
          const arrayBuffer = e.target.result as ArrayBuffer;
          const result = await mammoth.extractRawText({ arrayBuffer });
          const cleanedText = textCleanUpService.createSample(result.value);
          resolve(cleanedText);
        } catch (error) {
          reject(new Error(`DOCX extraction failed: ${error instanceof Error ? error.message : 'Unknown error'}`));
        }
      };

      reader.onerror = () => reject(new Error('Error reading DOCX file'));
      reader.readAsArrayBuffer(file);
    })
  );
}

export function extractFromEml(file: File): Observable<string> {
  return emlExtractorService.extractText(file)
      .pipe(
          catchError(error => throwError(() => new Error(`EML extraction failed: ${error.message || 'Unknown error'}`)))
      );
}

/**
 * Extracts text from an MSG file (Outlook message)
 */
export function extractFromMsg(file: File): Observable<string> {
  return msgExtractorService.extractText(file)
      .pipe(
          catchError(error => throwError(() => new Error(`MSG extraction failed: ${error.message || 'Unknown error'}`)))
      );
}
